/**
 * App Store
 *
 * Main Zustand store combining all slices with persistence.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getInitialStateFromUrl } from '@/utils/urlParsing';
import type { AppStore } from './types';
import {
  createViewSlice,
  createFilterSlice,
  createModalSlice,
  createNavigationSlice,
  createItemSlice,
  createDeviationSlice,
  createUiSlice,
  createDataSlice,
  createComplexActionsSlice,
  createUndoSlice,
} from './slices';

// Re-export types for backward compatibility
export type { ItemType, SpaceItems } from './types';

const initialUrlState = getInitialStateFromUrl();

export const useAppStore = create<AppStore>()(
  persist(
    (...args) => {
      const viewSlice = createViewSlice(...args);
      const filterSlice = createFilterSlice(...args);
      const modalSlice = createModalSlice(...args);
      const navigationSlice = createNavigationSlice(...args);
      const itemSlice = createItemSlice(...args);
      const deviationSlice = createDeviationSlice(...args);
      const uiSlice = createUiSlice(...args);
      const dataSlice = createDataSlice(...args);
      const complexActionsSlice = createComplexActionsSlice(...args);
      const undoSlice = createUndoSlice(...args);

      return {
        // Combine all slices
        ...viewSlice,
        ...filterSlice,
        ...modalSlice,
        ...navigationSlice,
        ...itemSlice,
        ...deviationSlice,
        ...uiSlice,
        ...dataSlice,
        ...complexActionsSlice,
        ...undoSlice,

        // Override with URL initial state
        viewMode: initialUrlState.viewMode,
        selectedTaskId: initialUrlState.taskId,
        filters: {
          ...filterSlice.filters,
          path: initialUrlState.path,
        },
        deviationGroupBy: initialUrlState.deviationGroupBy || null,
        deviationFilter: initialUrlState.deviationFilter || 'all',
        deviationAssignee: initialUrlState.deviationAssignee || null,
      };
    },
    {
      name: 'zenda-app-store',
      partialize: (state) => ({
        viewMode: state.viewMode,
        filters: state.filters,
        grouping: {
          ...state.grouping,
          collapsedGroups: Array.from(state.grouping.collapsedGroups),
        },
        sorting: state.sorting,
        theme: state.theme,
        palette: state.palette,
        spaces: state.spaces,
        categories: state.categories,
        foldersMap: state.foldersMap,
        listsMap: state.listsMap,
        deviationGroupBy: state.deviationGroupBy,
        deviationFilter: state.deviationFilter,
        deviationAssignee: state.deviationAssignee,
        sidebarStats: state.sidebarStats,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.grouping?.collapsedGroups && !(state.grouping.collapsedGroups instanceof Set)) {
          state.grouping.collapsedGroups = new Set(state.grouping.collapsedGroups as unknown as string[]);
        }
        if (state && !Array.isArray(state.spaces)) {
          state.spaces = [];
        }
        if (state && !Array.isArray(state.categories)) {
          state.categories = [];
        }
        if (state && !state.foldersMap) {
          state.foldersMap = {};
        }
        if (state && !state.listsMap) {
          state.listsMap = {};
        }
        if (state && !state.sidebarStats) {
          state.sidebarStats = { overdue: 0, today: 0, inProgress: 0 };
        }
        // If spaces were rehydrated from cache, skip the loading spinner
        if (state && Array.isArray(state.spaces) && state.spaces.length > 0) {
          state.spacesLoading = false;
        }
        // Re-apply URL state after rehydration to prevent persisted state
        // from overwriting what the user navigated to via hard URL
        if (state) {
          const urlState = initialUrlState;
          state.viewMode = urlState.viewMode;
          state.selectedTaskId = urlState.taskId;
          state.filters = {
            ...state.filters,
            path: urlState.path,
          };
          if (urlState.deviationGroupBy !== undefined) {
            state.deviationGroupBy = urlState.deviationGroupBy || null;
          }
          if (urlState.deviationFilter !== undefined) {
            state.deviationFilter = urlState.deviationFilter || 'all';
          }
          if (urlState.deviationAssignee !== undefined) {
            state.deviationAssignee = urlState.deviationAssignee || null;
          }
        }
      },
    }
  )
);
